import { useState, useEffect, useId, useRef } from "react";
import { Dialog } from "@headlessui/react";
import { useTheme } from '../../CommonComponents/ThemeProvider';
import tinycolor from "tinycolor2";
import { Box, Camera, ImagePlus, Loader2, Trash2, X } from "lucide-react";

export default function AddProductModal({ isOpen, onClose, onSubmit, editData }) {
    const { currentTheme } = useTheme();
    const primaryColor = currentTheme.color;
    const hoverColor = tinycolor(primaryColor).darken(10).toString();
    const uploadsBaseURL = `${process.env.REACT_APP_API_URL}/uploads`;
    const uploadInputId = useId();
    const mountedRef = useRef(false);

    const [productData, setProductData] = useState({
        name: "",
        price: "",
        unit_type: "",
        description: "",
        image: null,
    });

    const [preview, setPreview] = useState(null);
    const [removeImage, setRemoveImage] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (editData) {
            const existingImage =
                editData.image
                || (editData.image_url
                    ? (String(editData.image_url).startsWith("http")
                        ? editData.image_url
                        : `${uploadsBaseURL}/${editData.image_url}`)
                    : null);

            setProductData({
                name: editData.name || "",
                price: editData.price || "",
                unit_type: editData.unit_type || "",
                description: editData.description || "",
                image: null,
            });
            setPreview(existingImage);
            setRemoveImage(false);
        } else {
            setProductData({ name: "", price: "", unit_type: "", description: "", image: null });
            setPreview(null);
            setRemoveImage(false);
        }
        setSubmitting(false);
    }, [editData, uploadsBaseURL]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setProductData({ ...productData, [name]: value });
    };

    const handleImageChange = (file) => {
        setProductData({ ...productData, image: file });
        setPreview(file ? URL.createObjectURL(file) : null);
        setRemoveImage(false);
    };

    const handleImageRemove = () => {
        setProductData({ ...productData, image: null });
        setPreview(null);
        setRemoveImage(Boolean(editData?.image_url || editData?.image));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;

        const formData = new FormData();
        formData.append("name", productData.name);
        formData.append("price", productData.price);
        formData.append("unit_type", productData.unit_type);
        formData.append("description", productData.description);
        formData.append("removeImage", removeImage ? "true" : "false");

        if (productData.image) {
            formData.append("image", productData.image);
        }

        setSubmitting(true);
        try {
            await onSubmit(formData);
        } finally {
            if (mountedRef.current) setSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <Dialog
            open={isOpen}
            onClose={() => {
                if (!submitting) onClose();
            }}
            className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/45 px-4 py-4 backdrop-blur-sm"
        >
            <div className="flex min-h-full w-full items-center justify-center">
                <Dialog.Panel className="relative my-auto max-h-[calc(100vh-2rem)] w-full max-w-lg overflow-y-auto rounded-[28px] border border-white/70 bg-white shadow-[0_26px_70px_rgba(15,23,42,0.16)]">
                    <div className="absolute inset-x-0 top-0 h-28 bg-[linear-gradient(135deg,rgba(15,23,42,0.05),rgba(148,163,184,0.04),transparent)]" />
                    <button
                        type="button"
                        onClick={() => {
                            if (!submitting) onClose();
                        }}
                        disabled={submitting}
                        className="absolute right-4 top-4 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white/95 text-slate-500 shadow-sm transition hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
                        aria-label="Close product modal"
                    >
                        <X className="h-5 w-5" />
                    </button>

                    <div className="relative p-3.5">
                        <div className="flex flex-col gap-3">
                            <div className="flex items-start gap-2.5">
                                <div
                                    className="inline-flex h-11 w-11 items-center justify-center rounded-2xl text-white shadow-sm"
                                    style={{ background: `linear-gradient(135deg, ${primaryColor}, ${hoverColor})` }}
                                >
                                    <Box className="h-4.5 w-4.5" />
                                </div>
                                <div className="min-w-0">
                                    <Dialog.Title className="text-lg font-semibold tracking-tight text-slate-900">
                                        {editData ? "Edit Product" : "Add Product"}
                                    </Dialog.Title>
                                    <p className="mt-0.5 max-w-md text-[13px] leading-4.5 text-slate-500">
                                        {editData
                                            ? "Update the product details and refresh its image without leaving this workspace."
                                            : "Create a polished product listing with complete details and an optional image."}
                                    </p>
                                </div>
                            </div>

                            <form onSubmit={handleSubmit} className="grid gap-3">
                                <fieldset disabled={submitting} className="contents disabled:cursor-wait">
                                <div className="grid gap-2.5 rounded-[22px] border border-slate-200 bg-slate-50/80 p-3 sm:grid-cols-2">
                                    <label className="block sm:col-span-2">
                                        <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                                            Product Name
                                        </span>
                                        <input
                                            name="name"
                                            placeholder="Enter product name"
                                            value={productData.name}
                                            onChange={handleChange}
                                            className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 shadow-sm transition focus:outline-none"
                                            style={{ boxShadow: "0 8px 20px rgba(15,23,42,0.04)" }}
                                            required
                                        />
                                    </label>

                                    <label className="block">
                                        <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                                            Price
                                        </span>
                                        <input
                                            name="price"
                                            type="number"
                                            placeholder="0.00"
                                            value={productData.price}
                                            onChange={handleChange}
                                            className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 shadow-sm transition focus:outline-none"
                                            style={{ boxShadow: "0 8px 20px rgba(15,23,42,0.04)" }}
                                            min="0"
                                            step="0.01"
                                            required
                                        />
                                    </label>

                                    <label className="block">
                                        <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                                            Unit Type
                                        </span>
                                        <select
                                            name="unit_type"
                                            value={productData.unit_type}
                                            onChange={handleChange}
                                            className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 shadow-sm transition focus:outline-none"
                                            style={{ boxShadow: "0 8px 20px rgba(15,23,42,0.04)" }}
                                            required
                                        >
                                            <option value="">Select unit type</option>
                                            <option value="pcs">Piece</option>
                                            <option value="kg">Kg</option>
                                            <option value="liter">Liter</option>
                                            <option value="pack">Pack</option>
                                        </select>
                                    </label>

                                    <label className="block sm:col-span-2">
                                        <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                                            Description
                                        </span>
                                        <textarea
                                            name="description"
                                            placeholder="Add a short product description"
                                            value={productData.description}
                                            onChange={handleChange}
                                            className="min-h-[60px] w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 shadow-sm transition focus:outline-none"
                                            style={{ boxShadow: "0 8px 20px rgba(15,23,42,0.04)" }}
                                            rows={1}
                                        />
                                    </label>
                                </div>

                                <div className="rounded-[22px] border border-slate-200 bg-white p-3 shadow-sm">
                                    <div className="mb-2.5 flex items-center gap-2.5">
                                        <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-slate-100 text-slate-500">
                                            <ImagePlus className="h-4.5 w-4.5" />
                                        </div>
                                        <div>
                                            <h3 className="text-sm font-semibold text-slate-900">Product Image</h3>
                                            <p className="text-xs leading-4 text-slate-500">
                                                Upload a square or portrait image for a cleaner product card preview.
                                            </p>
                                        </div>
                                    </div>

                                    <input
                                        id={uploadInputId}
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={(e) => {
                                            const file = e.target.files?.[0];
                                            if (file) handleImageChange(file);
                                        }}
                                    />

                                    <div className="min-w-0 flex flex-col gap-3 sm:flex-row sm:items-center">
                                        <div className="relative mx-auto sm:mx-0">
                                            <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_10px_24px_rgba(15,23,42,0.08)]">
                                                {preview ? (
                                                    <img
                                                        src={preview}
                                                        alt="Product preview"
                                                        className="h-full w-full object-cover"
                                                    />
                                                ) : (
                                                    <div className="flex h-full w-full items-center justify-center bg-slate-100 text-slate-400">
                                                        <ImagePlus className="h-9 w-9" />
                                                    </div>
                                                )}
                                            </div>
                                            {preview ? (
                                                <button
                                                    type="button"
                                                    onClick={handleImageRemove}
                                                    className="absolute -right-2 -top-2 inline-flex h-8 w-8 items-center justify-center rounded-full border border-white/80 bg-white text-slate-500 shadow-lg shadow-slate-200/80 transition hover:bg-rose-50 hover:text-rose-600 focus:outline-none"
                                                    aria-label="Delete product image"
                                                >
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </button>
                                            ) : null}
                                        </div>

                                        <div className="min-w-0 flex-1 overflow-hidden">
                                            <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center">
                                                <label
                                                    htmlFor={uploadInputId}
                                                    className={`inline-flex shrink-0 items-center justify-center gap-2 rounded-2xl bg-slate-900 px-4 py-2 text-sm font-medium text-white shadow-[0_10px_20px_rgba(15,23,42,0.14)] transition hover:bg-slate-800 ${submitting ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
                                                >
                                                    <Camera className="h-4 w-4" />
                                                    Choose Image
                                                </label>
                                                <div className="min-w-0 w-full flex-1 overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500 sm:w-auto sm:max-w-[220px]">
                                                    <span className="block w-full overflow-hidden text-ellipsis whitespace-nowrap">
                                                        {productData.image?.name || "No file selected"}
                                                    </span>
                                                </div>
                                            </div>
                                            <p className="mt-2 text-xs leading-4 text-slate-500">
                                                PNG or JPG works best. Leave this unchanged to keep the current image.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex justify-end gap-2 pt-0.5">
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        disabled={submitting}
                                        className="rounded-2xl border border-slate-200 bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-200 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className="inline-flex min-w-[132px] items-center justify-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold text-white shadow-[0_14px_30px_rgba(15,23,42,0.16)] transition hover:opacity-90 focus:outline-none disabled:cursor-wait disabled:opacity-75"
                                        style={{ background: `linear-gradient(135deg, ${primaryColor}, ${hoverColor})` }}
                                    >
                                        {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                                        {submitting
                                            ? (editData ? "Updating..." : "Adding...")
                                            : (editData ? "Update Product" : "Add Product")}
                                    </button>
                                </div>
                                </fieldset>
                            </form>
                        </div>
                    </div>
                </Dialog.Panel>
            </div>
        </Dialog>
    );
}
